import { addDays, parseDate, toDateStr, toMonday } from './ganttDates';
import { formatDateWeekdayDayMonth } from './dateFormat';

/**
 * TIMESHEETS — the client's DISPLAY vocabulary (stage S3b). Labels, field
 * kinds, the week arithmetic and the crew day-status derivation, in one place
 * so the crew and manager screens cannot drift apart.
 *
 * 🔒 Nothing here prices anything or decides who may do what. The server owns
 * statuses, conflicts and money (`server/helpers/timesheetRules.js`); this file
 * only turns the server's facts into words.
 */

// ── the person-day fields, in the server's order (timesheetRules.js PERSON_FIELDS) ──

export const UNIT_FIELD = 'sub_department_id';

/**
 * kind: 'unit' | 'day_type' | 'count' | 'time' | 'hours' | 'tick'.
 * 🔻 `travel_to_paid` / `travel_from_paid` are GONE from the client (owner,
 * 2026-10-25: a travel value is paid, 0/blank is not — no tick, no "(Paid)").
 * The server keeps and ignores them (`timesheetRules.js#IGNORED_FLAG_FIELDS`);
 * the client neither shows nor sends them.
 */
export const FIELD_DEFS = Object.freeze([
  { field: 'sub_department_id', label: 'Department', kind: 'unit' },
  { field: 'day_type', label: 'Day type', kind: 'day_type' },
  // Owner 2026-10-26/27: SWD / SCWD / CWD / Flat can change any day (blank = SWD);
  // the production says Film or TV. "Working day" — not a list of the choices.
  { field: 'working_day', label: 'Working day', kind: 'working_day' },
  { field: 'day_number', label: 'Day #', kind: 'count' },
  { field: 'unit_call', label: 'Unit call', kind: 'time' },
  { field: 'meal_start', label: 'Meal start', kind: 'time' },
  { field: 'meal_end', label: 'Meal end', kind: 'time' },
  { field: 'unit_wrap', label: 'Wrap', kind: 'time' },
  // "(h)" (owner 2026-10-28): the box takes HOURS — a list of 15-minute steps, or typed.
  { field: 'travel_to_hours', label: 'Travel to (h)', kind: 'hours' },
  { field: 'travel_from_hours', label: 'Travel from (h)', kind: 'hours' },
  { field: 'camera_ot_hours', label: 'Camera O/T', kind: 'hours' },
  { field: 'non_camera_ot_hours', label: 'Non-camera O/T', kind: 'hours' },
  { field: 'meal_penalty', label: 'Meal penalty', kind: 'tick' },
  { field: 'night_penalty', label: 'Night penalty', kind: 'tick' },
]);

/**
 * CALCULATED OVERTIME (owner 2026-10-26; the server's
 * `timesheetRules.js#PERSON_ONLY_FIELDS` / `OVERRIDE_FIELDS`). Pre-call / Final out / Flat
 * day are the PERSON's own; one that differs from the buyer's side is a conflict (2026-10-31). 🔀 2026-10-28 (owner): In → "Pre-call", Out →
 * "Final out", and the UNIT timecard has both too — a blank person value follows the unit's
 * (`UNIT_DEFAULT_FIELDS` on the server), then the unit's call / wrap. The three override fields
 * show the SERVER's calculation (`day.overtime`) until a value is stored; a stored value is a
 * manager override (blue, ↺ clears it). The client derives no hours.
 */
export const PERSON_ONLY_FIELD_DEFS = Object.freeze([
  { field: 'in_time', label: 'Pre-call', kind: 'time' },
  { field: 'out_time', label: 'Final out', kind: 'time' },
  { field: 'flat_day', label: 'Flat day', kind: 'tick' },
]);
export const OVERRIDE_FIELDS = Object.freeze(['camera_ot_hours', 'non_camera_ot_hours', 'meal_penalty']);

/** `productions.timesheet_agreement` — the words for the server's AGREEMENTS keys. */
// 🔀 2026-10-26 (owner): the production says only Film or TV; SWD/CWD is per day on the
// timecard. A stored film_cwd / tv_cwd reads as its family (`agreementFamilyValue`).
// 🔀 2026-10-28: no figures in the label — a production may change them (the panel below shows its own).
export const TIMESHEET_AGREEMENT_OPTIONS = Object.freeze([
  { value: 'film_swd', label: 'Film — Pact/Bectu film terms' },
  { value: 'tv_swd', label: 'TV — Pact/Bectu TV terms' },
]);
export function agreementFamilyValue(stored) {
  return String(stored || '').startsWith('tv') ? 'tv_swd' : 'film_swd';
}
/** A BLANK Working day reads "Flat" (owner 2026-10-27: "Default should be Flat"). */
export const BLANK_FLAT_TITLE = 'Blank = Flat — a normal day’s pay, no overtime. Pick SWD, SCWD, CWD or Workshop when the hours count.';

/**
 * WORKSHOP ON A SHOOTING UNIT (owner 2026-10-28, option A): Workshop is an OFF-SET day, so picking it
 * on a shooting unit quietly drops camera O/T. Say so on the Working day cell; a day on set leaves
 * Working day to the unit's SWD/SCWD/CWD.
 */
export const WORKSHOP_ON_SET_WARNING = 'Workshop is an off-set day — no camera O/T. On set today? Leave Working day to the unit’s.';
export function workshopOnShootingUnit(workingDay, unit) {
  return workingDay === 'workshop' && !!unit && Number(unit.is_shooting_unit) === 1;
}

export const WORKING_DAY_OPTIONS = Object.freeze([
  { value: 'swd', label: 'SWD' },
  { value: 'scwd', label: 'SCWD' },
  { value: 'cwd', label: 'CWD' },
  // Owner 2026-10-28: a Workshop day, measured at 10+1, off set (In→Out, no camera O/T).
  { value: 'workshop', label: 'Workshop' },
  // 🔻 'flat' REMOVED as a choice (owner 2026-10-27: "2 flats … remove the one at the bottom") —
  // the blank "Flat (default)" is Flat. A day already stored as 'flat' still reads and pays as Flat.
]);

const DEF_BY_FIELD = new Map([...FIELD_DEFS, ...PERSON_ONLY_FIELD_DEFS].map((d) => [d.field, d]));

/**
 * THE week grid's rows, in three groups separated by a rule (owner-approved
 * mock, 2026-10-25, after his reference timecard): the day · the times in the
 * order a day happens · overtime and penalties. Unit call and Wrap are bold.
 * The Unit row is the PERSON grid's only (a unit is not its own unit).
 * 🔀 2026-10-28 (owner: "put the precall before unit call and final out after wrap"): Pre-call
 * sits directly above Unit call and Final out directly below Wrap, on EVERY grid — the unit
 * timecard included (they are its defaults now) — and in the phone day view (same groups).
 */
export const GRID_ROW_GROUPS = Object.freeze([
  ['sub_department_id', 'day_type', 'working_day', 'day_number'],
  ['travel_to_hours', 'in_time', 'unit_call', 'meal_start', 'meal_end', 'unit_wrap', 'out_time', 'travel_from_hours'],
  ['camera_ot_hours', 'non_camera_ot_hours', 'meal_penalty', 'night_penalty'],
]);
export const GRID_BOLD_FIELDS = Object.freeze(['unit_call', 'unit_wrap']);
/**
 * A FLAT working day greys these out (owner 2026-10-27: "if selected it should grey out all
 * other inputs as they are not required"): no hours are measured, so no times and no O/T.
 * Travel and the night penalty stay editable (paid apart from the day).
 */
export const FLAT_GREYED_FIELDS = Object.freeze(['unit_call', 'meal_start', 'meal_end', 'unit_wrap', 'in_time', 'out_time', 'camera_ot_hours', 'non_camera_ot_hours', 'meal_penalty']);
/**
 * Ruling 1A (owner 2026-10-29): a person's Pre-call / Final out is STORED AS TYPED, even when it
 * equals the unit's — the server never re-links these (`timesheetRules.js#UNIT_DEFAULT_FIELDS`).
 * So on a person's grid an EDITED equal value is a real draft; an untouched cell still sends nothing.
 */
export const TYPED_AS_STORED_FIELDS = Object.freeze(['in_time', 'out_time']);
/** Owner 2026-10-26: "a rest day should clear the times" — picking Rest clears these in that column. */
export const REST_CLEARS_FIELDS = Object.freeze(['working_day', 'travel_to_hours', 'unit_call', 'meal_start', 'meal_end', 'unit_wrap', 'travel_from_hours', 'in_time', 'out_time']);

/**
 * THE FOLDED GROUP (owner 2026-10-25, option B, to fit a 1366×657 laptop): the
 * overtime-and-penalties rows collapse into ONE "O/T & penalties ▸" row on
 * every week grid, COLLAPSED by default and OPEN by itself ONLY for a problem
 * (owner 2026-10-25): a conflict or a required blank inside it — and while the
 * user holds an unsaved draft there, so their edit never vanishes
 * (`foldCellNeedsOpen`). 🔀 2026-10-26: a non-zero CALCULATED O/T or penalty
 * (or an override of one) opens it too. Typed values alone (Night penalty,
 * Notes) never do: collapsed, the one-line summary carries them ("NP · Note").
 * 🔑 An explicit `0` hours is a real value but NOT something to see: zero
 * overtime is the normal day, and opening on it would keep the rows open on
 * almost every week (the fixtures and real weeks carry 0s).
 */
export const GRID_FOLD = Object.freeze({
  label: 'O/T & penalties',
  // Owner 2026-10-25: Notes joins the fold where a grid has it (the person
  // grids), and the label then says so; Job sits under Unit instead ("move job
  // to under unit"). The unit timecard has neither.
  labelWithNotes: 'O/T, penalties & notes',
  // `rest` (calculated overtime, 2026-10-26) rides in the fold on the person grids.
  // `ot_total` (owner 2026-10-27, "split the hours"): the day's whole O/T, above the camera split.
  fields: Object.freeze(['ot_total', ...GRID_ROW_GROUPS[2], 'rest', 'notes']),
  noteFields: Object.freeze(['notes']),
  short: Object.freeze({ camera_ot_hours: 'O/T', non_camera_ot_hours: 'Non-cam O/T', meal_penalty: 'MP', night_penalty: 'NP', notes: 'Note', rest: 'Rest' }),
});

/**
 * A folded cell that OPENS the fold by itself: a conflict, a required blank, an
 * unsaved draft — and (owner 2026-10-26: "should auto expand as soon as any OT
 * or penalty are calculated") a calculated figure, override or warning that is
 * not quiet (a calculated "–" zero and a "No" override stay folded).
 */
const FOLD_OPEN_TONES = Object.freeze(['computed', 'override', 'warn']);
export function foldCellNeedsOpen(cell) {
  if (!cell) return false;
  if (cell.conflict || cell.required || cell.drafted) return true;
  return FOLD_OPEN_TONES.includes(cell.tone) && cell.text != null && !cell.quiet;
}

/** A folded cell worth a word in the summary: a non-zero value, a tick, "Mixed", a draft, a conflict, a required blank. */
export function foldCellHasContent(cell) {
  if (!cell) return false;
  if (cell.conflict || cell.required || cell.drafted) return true;
  if (cell.text == null || cell.text === '') return false;
  const n = Number(cell.text);
  return !(Number.isFinite(n) && n === 0);
}

/** The collapsed row's one line for a day: `"O/T 0.5 · MP"`, or `null` when nothing is set. */
export function foldDaySummary(rows, date, getCell) {
  const parts = [];
  for (const row of rows) {
    const cell = getCell(row, date);
    // A `quiet` cell (rest "ok", an explicit "No" override) is not news in a one-line summary.
    if (!cell || cell.quiet || cell.text == null || cell.text === '' || !foldCellHasContent({ text: cell.text })) continue;
    const short = GRID_FOLD.short[row.key] || row.label;
    // A tick or a Note is a MARKER ("MP", "Note"); hours carry their value.
    parts.push(row.kind === 'tick' || GRID_FOLD.noteFields.includes(row.key) ? short : `${short} ${cell.text}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

/**
 * One value as a GRID CELL shows it: plain text, no unit suffix (the row label
 * says what it is), a tick as ✓ and an unticked tick as blank. `null` = blank.
 */
export function gridCellText(field, value, unitName) {
  if (value === null || value === undefined || value === '') return null;
  const kind = fieldKind(field);
  if (kind === 'tick') return value === true || value === '1' || value === 1 ? '✓' : null;
  if (kind === 'hours') return String(value);
  return formatFieldValue(field, value, unitName);
}

/**
 * The words for the keys of a SERVER-priced day's `cost.lines`
 * (`timesheetRules.js#priceDay`). The manager screen prints those figures
 * as sent; nothing here (or anywhere in the client) multiplies a rate.
 */
export const COST_LINE_LABELS = Object.freeze([
  ['base', 'Day rate'],
  ['camera_ot', 'Camera O/T'],
  ['non_camera_ot', 'Non-camera O/T'],
  ['travel_to', 'Travel to'],
  ['travel_from', 'Travel from'],
  ['meal_penalty', 'Meal penalty'],
  ['night_penalty', 'Night penalty'],
]);

/** The words for the server's `missing` rate names ("Rate missing" — never priced at £0). */
const MISSING_RATE_TEXT = Object.freeze({
  crew_row: 'not on the current budget version',
  day_rate: 'day rate',
  hourly_rate: 'hourly rate',
  camera_ot_multiplier: 'camera O/T multiplier',
  non_camera_ot_multiplier: 'non-camera O/T multiplier',
  travel_multiplier: 'travel multiplier',
  meal_penalty_multiplier: 'meal penalty multiplier',
  night_penalty: 'night penalty amount',
});

export function missingRatesText(missing) {
  if (!Array.isArray(missing) || missing.length === 0) return null;
  return missing.map((m) => MISSING_RATE_TEXT[m] || String(m).replace(/_/g, ' ')).join(', ');
}

/** The unit timecard's fields — every person field except the unit itself. */
export const UNIT_TIMECARD_FIELDS = Object.freeze(FIELD_DEFS.filter((d) => d.field !== UNIT_FIELD));

export function fieldLabel(field) {
  const d = DEF_BY_FIELD.get(field);
  return d ? d.label : field;
}

export function fieldKind(field) {
  const d = DEF_BY_FIELD.get(field);
  return d ? d.kind : null;
}

export const DAY_TYPE_OPTIONS = Object.freeze([
  { value: 'shoot', label: 'Shoot' },
  { value: 'prep', label: 'Prep' },
  { value: 'rest', label: 'Rest' },
  { value: 'travel', label: 'Travel' },
]);

export const AWAY_REASON_OPTIONS = Object.freeze([
  { value: 'holiday', label: 'Holiday' },
  { value: 'sick', label: 'Sick' },
  { value: 'off_contract', label: 'Off contract' },
  { value: 'other', label: 'Other' },
]);

const AWAY_REASON_LABELS = Object.fromEntries(AWAY_REASON_OPTIONS.map((o) => [o.value, o.label]));

export function awayReasonLabel(reason) {
  return AWAY_REASON_LABELS[reason] || 'Away';
}

/**
 * One field value as words. `unitName(id)` resolves a unit id. A blank value
 * is `null` (the caller chooses its own marker).
 */
export function formatFieldValue(field, value, unitName) {
  if (value === null || value === undefined || value === '') return null;
  switch (fieldKind(field)) {
    case 'unit': return (unitName && unitName(Number(value))) || `Unit ${value}`;
    case 'day_type': {
      const o = DAY_TYPE_OPTIONS.find((t) => t.value === value);
      return o ? o.label : String(value);
    }
    case 'working_day': return value === 'flat' ? 'Flat' : String(value).toUpperCase();
    case 'tick': return value === true ? 'Yes' : 'No';
    case 'hours': return `${value} h`;
    default: return String(value);
  }
}

/** A unit's name from the department's unit list, or null. */
export function unitNameIn(units, id) {
  const u = (units || []).find((x) => Number(x.id) === Number(id));
  return u ? u.name : null;
}

// ── statuses ────────────────────────────────────────────────────────────────

/**
 * The person-week statuses, in the manager's tab order. `away` is the sixth
 * server status (an open "until further notice" Away over the whole week) —
 * the owner's five tabs plus it, so the tab counts sum to the list (ruled
 * 2026-10-24, S3b receipt item 4(i)).
 */
export const WEEK_STATUS_TABS = Object.freeze([
  // The KEY stays `not_entered` (no API churn); the WORDS are the owner's
  // (2026-10-25: "Maybe 'Not Entered' should change to 'To Complete'").
  { id: 'not_entered', label: 'To complete' },
  { id: 'conflicting', label: 'Conflicting' },
  { id: 'rate_missing', label: 'Rate missing' },
  { id: 'reconciled', label: 'Reconciled' },
  // The buyer's final step is "Approve" (owner, 2026-10-25); the key stays `submitted`.
  { id: 'submitted', label: 'Submitted' },
  { id: 'away', label: 'Away' },
]);

const WEEK_STATUS_LABELS = Object.fromEntries(WEEK_STATUS_TABS.map((t) => [t.id, t.label]));

/**
 * RC-2 (owner 2026-10-30: "any conflicts should be in the to reconcile section, buyer has final
 * say"): a person-week belongs in the Conflicting list when it IS conflicting, or when it is a
 * SUBMITTED week a crew member's own entry moved (`crew_knock_on`, server-published) — until the
 * buyer resubmits or reopens it.
 */
export function listedAsConflicting(p) {
  return !!p && (p.status === 'conflicting' || !!p.crew_knock_on);
}

export function weekStatusLabel(status) {
  return WEEK_STATUS_LABELS[status] || status;
}

/** Pill classes per status — every colour paired with its `dark:` shade. */
export const STATUS_PILL_COLOURS = Object.freeze({
  not_entered: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  conflicting: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  rate_missing: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  reconciled: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  submitted: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  away: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  not_needed: 'bg-white text-gray-500 border border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600',
});

export const CREW_DAY_STATUS_LABELS = Object.freeze({
  submitted: 'Submitted',
  away: 'Away',
  conflicting: 'Conflicting',
  not_entered: 'To complete',
  in_progress: 'In progress',
  not_needed: 'Not needed',
});

/**
 * THE crew day status — a DISPLAY derivation from the server's own day facts
 * (coordinator ruling 2026-10-24: the server has no per-day status). Order:
 * a submitted week wins, then Away, then a conflict, then an expected day
 * with nothing complete, then "in progress" for anything entered or complete;
 * a day nobody expects and nobody touched is "not needed".
 */
export function crewDayStatus(day, weekStatus) {
  if (weekStatus === 'submitted') return 'submitted';
  if (!day) return 'not_needed';
  if (day.away) return 'away';
  if (Array.isArray(day.conflicts) && day.conflicts.length > 0) return 'conflicting';
  if (day.expected && !day.complete) return 'not_entered';
  const touched = day.fields && Object.values(day.fields).some((f) => f && f.source != null);
  if (day.complete || touched || day.gag || day.notes) return 'in_progress';
  return 'not_needed';
}

// ── the crew hand-in (2026-10-25): a SECOND axis beside the status ──────────

/** `crew_state` words + pill colours (the hand-in axis; never in the status precedence). */
export const CREW_STATE_LABELS = Object.freeze({ draft: 'Draft', sent: 'Sent to buyer', taken: 'Taken over by buyer' });
export const CREW_STATE_PILL_COLOURS = Object.freeze({
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  sent: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  taken: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
});
/** The manager's extra tabs over the hand-in axis (they overlap the status tabs on purpose). */
export const CREW_STATE_TABS = Object.freeze([
  { id: 'crew:sent', label: 'Sent', crewState: 'sent' },
  { id: 'crew:taken', label: 'Taken over', crewState: 'taken' },
]);

/** Days of a week with required values still blank: [{ date, fields: [label, …] }]. */
/**
 * THE SOFT "IS THIS DAY COMPLETE?" WITH UNSAVED EDITS (owner 2026-10-30: "Can the completness bar
 * update as changes are made, not just when saved is cliked. So soft upate … nothing persists
 * unless save or submit week are clicked"). A MIRROR of the server's rule
 * (`server/helpers/timesheetRules.js#dayState`, `complete`) — a convenience, NEVER a gate: with no
 * draft for the day it answers the server's own `day.complete`, and the server decides again on
 * save. Held in step by a case table run through BOTH (`timesheetDisplay.softComplete.test.js` and
 * `server/tests/helpers/timesheetSoftComplete.parity.test.js`). The rule: Away / Rest (typed, or an untouched
 * weekend) / Flat → complete; a blank Working day → a unit and a Day type; a PICKED one → a unit and
 * a call (its wrap is always there: typed, the unit's, or the server's auto wrap).
 * `draft` = the grid's `{ fields: { [field]: raw } }` — blank raw = clear = follows the unit again.
 */
/** A PICKED Working day (needs times); blank or 'flat' is Flat. Mirrors the server's WORKING_DAY_VALUES. */
export const PICKED_WORKING_DAYS = Object.freeze(['swd', 'scwd', 'cwd', 'workshop']);
const SOFT_PICKED_WORKING_DAYS = PICKED_WORKING_DAYS;

/**
 * THE red * (owner 2026-10-30: "put a * next to items that need to be filled out"): marks the rows a
 * day needs to count (the rule above) — Day type always; Unit call once any shown day picks a Working
 * day (a Flat day needs no times; a wrap is never needed — blank is the auto wrap). `extra` adds the
 * Department on a person's grid (a unit timecard IS the unit). Returns new groups; marks only.
 */
export function markRequiredRows(groups, anyDayPicked, extra = []) {
  const need = new Set([...extra, 'day_type', ...(anyDayPicked ? ['unit_call'] : [])]);
  return groups.map((g) => g.map((r) => (need.has(r.key) ? { ...r, required: true } : r)));
}

export function softDayComplete(day, draft) {
  if (!day) return false;
  const drafted = (draft && draft.fields) || {};
  if (!Object.keys(drafted).length) return day.complete === true;
  if (day.away) return true;
  const fields = day.fields || {};
  const v = (f) => {
    if (drafted[f] === undefined) return fields[f] ? fields[f].value ?? null : null;
    const raw = drafted[f];
    if (typeof raw === 'boolean') return raw;
    if (raw === null || String(raw).trim() === '') return fields[f] ? fields[f].unit_value ?? null : null;
    return String(raw).trim();
  };
  const dayType = v('day_type');
  const workingDay = v('working_day');
  const call = v('unit_call');
  const typedWrap = v('unit_wrap');
  const flatTick = v('flat_day') === true || v('flat_day') === '1';
  const dow = new Date(`${day.date}T00:00:00Z`).getUTCDay();
  const defaultRest = (dow === 0 || dow === 6) && dayType === null && call === null && typedWrap === null
    && workingDay === null && !flatTick;
  if (dayType === 'rest' || defaultRest) return true;
  if (flatTick || workingDay === 'flat') return true;
  const unit = v('sub_department_id') !== null;
  if (!SOFT_PICKED_WORKING_DAYS.includes(workingDay)) return unit && dayType !== null;
  // A picked day with a READABLE call always has a wrap: typed, the unit's, or the server's auto
  // wrap. A half-typed call ("8am") gives the server no wrap, so it is not complete (review U1).
  return unit && call !== null && /^\d{1,2}:\d{2}$/.test(String(call).trim());
}

export function requiredBlankDays(days) {
  return (days || [])
    .filter((d) => Array.isArray(d.required_blank) && d.required_blank.length > 0)
    .map((d) => ({ date: d.date, fields: d.required_blank.map(fieldLabel) }));
}

// ── commit-all skips (store.commitAllReconciled: a status, or a refusal code) ──

const SKIP_REASON_TEXT = Object.freeze({
  not_entered: 'Days still to complete',
  // Lock week (2026-10-26): a Reconciled week with nothing in it is locked, not approved.
  reconciled: 'Nothing worked this week',
  conflicting: 'Conflicts to resolve',
  rate_missing: 'A rate is missing',
  submitted: 'Already approved',
  away: 'Away until further notice',
  week_submitted: 'Already approved',
  not_reconciled: 'Not reconciled',
  not_conflicting: 'No longer conflicting',
  changed: 'Changed since you looked — refresh and try again',
  // S3a F3: no day worked, none Away, nothing entered — left open, never locked at £0.
  nothing_to_commit: 'Nothing to commit — no days worked or away',
});

export function skipReasonText(reason) {
  return SKIP_REASON_TEXT[reason] || String(reason || 'Skipped').replace(/_/g, ' ');
}

/**
 * THE words for a field with no value — a BLANK unit value is now a real side of
 * a conflict (S3a F1), so both the conflict screen and the day form say it.
 */
export const NOT_SET_TEXT = 'Not set';

/**
 * The Away dialog's refusal text: the server's own `error`. 🔀 2026-10-29 (owner: Away follows the
 * edit-after-submit rule): the `week_submitted` arm (S3a F6) is DELETED with the refusal it
 * answered — an Away touching a submitted week is now allowed with a reason (`useEditReason`), and
 * the one Away refusal left about a submitted week (`edit_unsubmittable`) already says "reopen it".
 */
export function awayRefusalText(body) {
  return (body && body.error) || 'Could not set them as away.';
}

// ── weeks ───────────────────────────────────────────────────────────────────

/** The Monday (YYYY-MM-DD) of the week containing `date` (a Date or a YYYY-MM-DD). */
export function mondayOf(date) {
  const d = typeof date === 'string' ? parseDate(date) : date;
  return toDateStr(toMonday(d || new Date()));
}

export function shiftWeek(weekStart, weeks) {
  return toDateStr(addDays(parseDate(weekStart), weeks * 7));
}

export function weekDatesOf(weekStart) {
  const start = parseDate(weekStart);
  return [0, 1, 2, 3, 4, 5, 6].map((i) => toDateStr(addDays(start, i)));
}

/** "Mon 27 Oct" */
export function dayLabel(date) {
  return formatDateWeekdayDayMonth(date);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon" */
export function weekdayShort(date) {
  const d = parseDate(date);
  return d ? WEEKDAYS[d.getDay()] : '';
}

/**
 * The value a form control sends for a field. Blank → `null` (clears the
 * person's own value, so the field follows the unit again). Hours and times go
 * as the typed text: the server canonicalises them and refuses a malformed
 * one BY NAME, which a client-side `Number()` would turn into a silent clear.
 */
export function payloadValue(kind, raw) {
  // `null` clears for EVERY kind, a tick included (↺ on a meal-penalty override, 2026-10-26).
  if (raw === null) return null;
  if (kind === 'tick') return raw === true;
  if (raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (kind === 'unit') return Number(s);
  return s;
}

/** A field value as a form control holds it: a tick is a boolean, everything else text. */
export function controlValue(kind, v) {
  if (v === null || v === undefined) return kind === 'tick' ? false : '';
  return kind === 'tick' ? v === true : String(v);
}

// ── what a typed cell may hold (input audit + owner 2026-10-28) ──────────────

/**
 * 🪞 A MIRROR of `server/helpers/timesheetRules.js#isPlainHoursText` — the server owns the rule
 * and refuses by name (`invalid_hours`); this copy only lets the CELL refuse "2h" / "0x10" / "1,5"
 * at the field instead of at Save. Held byte-identical by
 * `server/tests/regressions/timesheet-hours-client-parity.test.js`; both suites run
 * `timesheetHoursCases.json`.
 */
const MAX_HOURS = 24;
const PLAIN_HOURS_RE = /^(\d+(\.\d*)?|\.\d+)$/;
function isPlainHoursText(text) {
  return typeof text === 'string' && PLAIN_HOURS_RE.test(text) && Number.isFinite(Number(text)) && Number(text) <= MAX_HOURS;
}
export { isPlainHoursText };

/** Day # (a count): a whole number, no sign, no point. 🪞 A MIRROR of the server's WRITE rule
 *  `timesheetRules.js#isPlainDayNumberText` (`invalid_day_number`), held identical by the same
 *  parity test as the hours rule. */
const MAX_DAY_NUMBER = 100000;
const PLAIN_DAY_NUMBER_RE = /^\d+$/;
function isPlainDayNumberText(text) {
  return typeof text === 'string' && PLAIN_DAY_NUMBER_RE.test(text) && Number(text) <= MAX_DAY_NUMBER;
}

/**
 * THE refusal a typed cell shows, or null when the text may be committed. Whitespace-only is BLANK
 * (a clear) and is fine. Hours: `isPlainHoursText`. Day #: a whole number. Never coerced.
 */
export const HOURS_PROBLEM = `Hours only — 0 to ${MAX_HOURS} (e.g. 1.5)`;
export const DAY_NUMBER_PROBLEM = 'A whole number only (e.g. 12)';
export function cellInputProblem(kind, text) {
  const s = text == null ? '' : String(text).trim();
  if (s === '') return null;
  if (kind === 'hours') return isPlainHoursText(s) ? null : HOURS_PROBLEM;
  if (kind === 'count') return isPlainDayNumberText(s) ? null : DAY_NUMBER_PROBLEM;
  return null;
}

/** Travel's list (owner 2026-10-28): 15-minute steps up to 8 hours; more can still be typed. */
export const TRAVEL_HOUR_OPTIONS = Object.freeze(Array.from({ length: 32 }, (_, i) => String((i + 1) / 4)));
const TRAVEL_FIELDS = Object.freeze(['travel_to_hours', 'travel_from_hours']);

/**
 * The extra row props a field's EDITOR needs on the week grids (`WeekTimecardGrid` row shape):
 * Travel (h) opens THE shared `ui/SuggestInput` with the 15-minute list, numbers only.
 * Every other field: none (its `kind` editor).
 */
export function fieldEditorProps(field) {
  if (!TRAVEL_FIELDS.includes(field)) return {};
  return {
    editor: 'suggest',
    options: TRAVEL_HOUR_OPTIONS,
    suggest: Object.freeze({
      inputMode: 'decimal',
      // Type "1.5", press Enter once: it saves (the open list closes WITHOUT eating the key).
      enterPassesThrough: true,
      placeholder: 'Hours, e.g. 1.5 — or pick below',
      listLabel: 'Hours',
      emptyText: () => 'No match — Done saves the hours you typed',
    }),
  };
}

const RANGE_TEXT = Object.freeze({
  hours: `hours only, 0 to ${MAX_HOURS} (e.g. 1.5)`,
  time: 'a time like 07:30',
  count: 'a whole number',
  tick: 'yes or no',
});

/**
 * A timesheet SAVE refusal in the user's words (input audit 2026-10-28): the field's LABEL, the
 * DAY and what is allowed — never the column name. The server names `field` and `work_date`
 * beside its own `error`; anything else shows the server's `error`, else `fallback`.
 */
export function saveErrorText(body, fallback) {
  if (body && body.field && DEF_BY_FIELD.has(body.field)) {
    const range = RANGE_TEXT[fieldKind(body.field)];
    const day = body.work_date ? ` on ${dayLabel(body.work_date)}` : '';
    if (range && /^invalid_/.test(String(body.code || ''))) return `${fieldLabel(body.field)}${day}: ${range}.`;
  }
  return (body && body.error) || fallback;
}

/** The Job picker's lists: titles to offer, and a lower-cased title → gag id map. */
export function gagPickList(gags) {
  const list = Array.isArray(gags) ? gags : [];
  return {
    gagTitles: list.map((g) => g.title).filter(Boolean),
    gagByTitle: new Map(list.map((g) => [String(g.title || '').trim().toLowerCase(), Number(g.id)])),
  };
}

/** Compact "08:00–19:00" for a day's effective call and wrap, or null. */
export function callWrapText(fields) {
  if (!fields) return null;
  const call = fields.unit_call && fields.unit_call.value;
  const wrap = fields.unit_wrap && fields.unit_wrap.value;
  if (!call && !wrap) return null;
  return `${call || '?'}–${wrap || '?'}`;
}
